export const TIER_ORDER = ['free', 'basic', 'pro', 'biz'];

export const TIERS = {
  free: {
    id:       'free',
    label:    'Free · Local',
    price:    null,
    interval: null,
  },
  basic: {
    id:       'basic',
    label:    'Freelancer Basic',
    price:    3,
    interval: 'month',
  },
  pro: {
    id:       'pro',
    label:    'Freelancer Pro',
    price:    6,
    interval: 'month',
  },
  biz: {
    id:       'biz',
    label:    'Business',
    price:    null,   // seat-based, TBD
    interval: null,
  },
};

// Each feature unlocks at 'tier' and all tiers above it.
export const FEATURES = [
  // ── Available on all tiers ──────────────────────────────────────────
  { id: 'time_tracking',   tier: 'free',  label: 'Punch in / punch out',             desc: 'Live clock-in with ISO 8601 timestamps' },
  { id: 'manual_entry',    tier: 'free',  label: 'Manual time entry',                desc: 'Back-fill entries with clock-in / clock-out times' },
  { id: 'timesheet_export', tier: 'free',  label: 'Timesheet report',                 desc: 'Tamper-proof PDF export of hours and entries' },
  { id: 'crypto_sign',     tier: 'free',  label: 'Cryptographic signing',            desc: 'Zero-knowledge ECDSA JWS seal on every batch' },
  { id: 'ocr_scan',        tier: 'free',  label: 'Document scanning (OCR)',          desc: 'On-device ML scan of pay stubs, timesheets, punch cards, schedules' },
  { id: 'offline_sync',    tier: 'free',  label: 'Offline-first storage',            desc: 'All data lives on-device; syncs to your Google Drive or OneDrive' },
  { id: 'invoices_5',      tier: 'free',  label: 'Up to 5 invoices / month',         desc: 'Client-side PDF generated and shared via device share sheet' },
  { id: 'ot_detection',    tier: 'free',  label: 'Overtime detection',               desc: 'Daily and weekly OT flagged per FLSA and state rules' },
  { id: 'schedule_track',  tier: 'free',  label: 'Schedule vs. actual tracking',     desc: 'Variance display; predictive scheduling violation flags' },

  // ── Freelancer Basic ────────────────────────────────────────────────
  { id: 'invoices_45',     tier: 'basic', label: 'Up to 45 invoices / month',        desc: 'Hosted on Cloudflare R2 with a shareable URL' },
  { id: 'invoice_hosting', tier: 'basic', label: 'Hosted invoice links',             desc: 'punchlog.app/invoice/:id, no file attachments needed' },
  { id: 'invoice_open',    tier: 'basic', label: 'Invoice open tracking',            desc: 'Know when a client views your invoice' },
  { id: 'email_delivery',  tier: 'basic', label: 'Email invoice delivery',           desc: 'Send invoices directly from the app via Cloudflare Email' },

  // ── Freelancer Pro ──────────────────────────────────────────────────
  { id: 'invoices_60',     tier: 'pro',   label: 'Up to 60 invoices / month',        desc: '' },
  { id: 'multi_client',    tier: 'pro',   label: 'Multi-client dashboard',           desc: 'Track hours and invoices across multiple clients in one view' },
  { id: 'webhooks',        tier: 'pro',   label: 'Webhook automation',               desc: 'HMAC-SHA256 signed events to Zapier, Make, or custom endpoints' },
  { id: 'webhook_retry',   tier: 'pro',   label: 'Webhook retry queue',              desc: '3 attempts with exponential backoff via Cloudflare Queues' },

  // ── Business ────────────────────────────────────────────────────────
  { id: 'teams',           tier: 'biz',   label: 'Team management',                  desc: 'Multi-tenant separation; admin vs. member roles' },
  { id: 'seat_billing',    tier: 'biz',   label: 'Seat-based billing',               desc: 'Manage team seats via Stripe Customer Portal' },
  { id: 'sms_alerts',      tier: 'biz',   label: 'SMS notifications',                desc: 'Outbound SMS via the native device, no Twilio cost' },
];

/** Returns true if userTier unlocks featureId. */
export function hasFeature(userTier, featureId) {
  const feature = FEATURES.find(f => f.id === featureId);
  if (!feature) return false;
  return TIER_ORDER.indexOf(userTier) >= TIER_ORDER.indexOf(feature.tier);
}

/** All features available at a given tier (inclusive of lower tiers). */
export function featuresForTier(tier) {
  const rank = TIER_ORDER.indexOf(tier);
  return FEATURES.filter(f => TIER_ORDER.indexOf(f.tier) <= rank);
}

/** Features grouped by tier, for display in settings. */
export function featuresByTier() {
  return TIER_ORDER.map(id => ({
    tier: TIERS[id],
    features: FEATURES.filter(f => f.tier === id),
  }));
}

/**
 * Comparison matrix for the plan table.
 * Each row cell: true = included (checkmark), false = not included (✗), string = custom label.
 */
export const FEATURE_GROUPS = [
  {
    label: 'Time Tracking',
    rows: [
      { label: 'Punch in / punch out',          free: true,  basic: true,  pro: true,  biz: true  },
      { label: 'Manual time entry',              free: true,  basic: true,  pro: true,  biz: true  },
      { label: 'Overtime detection (FLSA)',      free: true,  basic: true,  pro: true,  biz: true  },
      { label: 'Schedule vs. actual tracking',  free: true,  basic: true,  pro: true,  biz: true  },
    ],
  },
  {
    label: 'Compliance & Export',
    rows: [
      { label: 'Timesheet report PDF',           free: true,  basic: true,  pro: true,  biz: true  },
      { label: 'Cryptographic signing (ECDSA)',  free: true,  basic: true,  pro: true,  biz: true  },
      { label: 'Document scanning (OCR)',        free: true,  basic: true,  pro: true,  biz: true  },
      { label: 'Offline-first storage',          free: true,  basic: true,  pro: true,  biz: true  },
    ],
  },
  {
    label: 'Invoicing',
    rows: [
      { label: 'Invoices / month',     free: '5',      basic: '45',     pro: '60',     biz: '∞'    },
      { label: 'Hosted invoice links', free: false,    basic: true,     pro: true,     biz: true   },
      { label: 'Invoice open tracking',free: false,    basic: true,     pro: true,     biz: true   },
      { label: 'Email invoice delivery',free: false,   basic: true,     pro: true,     biz: true   },
    ],
  },
  {
    label: 'Automation',
    rows: [
      { label: 'Multi-client dashboard', free: false, basic: false,    pro: true,     biz: true   },
      { label: 'Webhook automation',     free: false, basic: false,    pro: true,     biz: true   },
      { label: 'Webhook retry queue',    free: false, basic: false,    pro: true,     biz: true   },
    ],
  },
  {
    label: 'Business',
    rows: [
      { label: 'Team management',    free: false, basic: false, pro: false, biz: true },
      { label: 'Seat-based billing', free: false, basic: false, pro: false, biz: true },
      { label: 'SMS notifications',  free: false, basic: false, pro: false, biz: true },
    ],
  },
];
